import styled, { css } from 'styled-components';
import { Typography } from 'src/typography';
import { BASE_MAX_WIDTH, PLAID_OFFSET } from 'src/payments/constants';
import { colors } from 'shared/themes';

const EMBEDDED_MAX_WIDTH = PLAID_OFFSET + BASE_MAX_WIDTH;
const ERROR_COLOR = '#D34A4A';

const getTabBorder = (error: boolean, selected: boolean, accentColor: string): string => {
  if (error) {
    return `2px solid ${ERROR_COLOR}`;
  }
  if (selected) {
    return `2px solid ${accentColor}`;
  }
  return `1px solid var(--dcm-checkout-payment-tab-border-color, ${String(colors.grey[85])})`;
};

const getTabContentBorder = (showEmbeddedSearch: boolean, error: boolean): string => {
  if (showEmbeddedSearch) {
    return 'none';
  }
  return error
    ? `1px solid ${ERROR_COLOR}`
    : `1px solid var(--dcm-checkout-payment-tab-border-color, ${colors.grey[85]})`;
};

// Common styles
const FLEX_CENTER = `
  display: flex;
  align-items: center;
`;

export const TabText = styled<{ selected: boolean }>(Typography.Body)`
  line-height: 20px;
  color: ${({ theme: { colors: themeColors }, selected }) =>
    selected
      ? `var(--dcm-checkout-section-heading-color, ${themeColors.grey[25]})`
      : `var(--dcm-checkout-text-muted-color, ${themeColors.grey[50]})`};
`;

export const TabTextGroup = styled.div<{ gap?: number }>`
  ${FLEX_CENTER}
  gap: ${({ theme: { spaces }, gap }) => spaces[gap ?? 1]};
  flex-shrink: 0;
`;

// Delete above
export const Badge = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    min-height: 20px;
    align-items: center;
    gap: ${spaces[2]};
    background: #eadaf2;
    border-radius: 14px;
  `}
`;

export const BadgeContent = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    padding: ${spaces[1]} ${spaces[2]};
    align-items: center;
    gap: ${spaces[1]};
  `}
`;

export const BadgeLabel = styled.div`
  color: #3c0059;
  font: 600 10px / normal Matter;
  letter-spacing: 0.05px;
`;

export const TabsContainer = styled.div<{ showEmbeddedSearch: boolean }>`
  display: flex;
  width: 100%;
  min-width: 320px;
  max-width: ${({ showEmbeddedSearch }) => (showEmbeddedSearch ? EMBEDDED_MAX_WIDTH : BASE_MAX_WIDTH)}px;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spaces } }) => spaces[3]};
`;

export const TabListSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  gap: ${({ theme: { spaces } }) => spaces[2]};
`;

export const TabList = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme: { spaces } }) => spaces[2]};
  align-self: stretch;
  max-width: ${BASE_MAX_WIDTH}px;
`;

export const Tab = styled.div<{ selected: boolean; error?: boolean; accentColor?: string }>`
  ${({ theme: { spaces, customized }, selected, error, accentColor = colors.primaryBlue }) => css`
    position: relative;
    display: flex;
    padding: ${spaces[3]};
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    align-content: space-between;
    height: 72px;
    border-radius: var(--dct--customized-radius-md, ${customized.radius.md});
    border: ${getTabBorder(!!error, selected, accentColor)};
    background: var(--dcm-checkout-payment-tab-bg, #fff);
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid ${colors.primaryBlue};
      outline-offset: 2px;
    }
  `}
`;

export const TabLabel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${({ theme: { spaces } }) => spaces[2]};
`;

export const RadioContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;

export const TabContent = styled.div<{ error?: boolean; showEmbeddedSearch?: boolean }>`
  ${({ theme: { customized }, error, showEmbeddedSearch }) => css`
    display: flex;
    min-height: 100px;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    border-radius: ${customized.radius.md};
    border: ${getTabContentBorder(!!showEmbeddedSearch, !!error)};
    background-color: ${showEmbeddedSearch ? 'transparent' : 'var(--dcm-checkout-payment-content-bg, #fff)'};
    margin-left: ${showEmbeddedSearch ? `-${PLAID_OFFSET}px` : '0'};

    &:focus-visible {
      outline: 2px solid ${customized?.colors?.buttonsLinks ?? colors.primaryBlue};
      outline-offset: 2px;
    }
  `}
`;

export const AlertContainer = styled.div<{ isEmbeddedSearch?: boolean }>`
  width: 100%;
  max-width: ${BASE_MAX_WIDTH}px;
  ${({ theme: { spaces }, isEmbeddedSearch }) =>
    isEmbeddedSearch &&
    css`
      padding: 0 ${String(spaces[1])};
      margin-top: ${String(spaces[1])};
    `}
`;

export const TabContainer = styled.div`
  position: relative;
  flex: 1 0 0;
`;
